import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  model,
  effect,
  inject,
  signal,
  OnInit,
  computed,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import { DateRangePickerComponent as LibDateRangePicker } from 'nebula-ui-kit';
import { catchError, of, tap, forkJoin, Subject, takeUntil } from 'rxjs';
import { AlertStore, TranslationService } from '@nebula-shared/data-access';
import { AnullDateRangePickerServices } from './src/lib/anull-date-range-picker.service';

@Component({
  standalone: true,
  imports: [LibDateRangePicker],
  selector: 'app-anull-date-range-picker',
  templateUrl: './anull-date-range-picker.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnullDateRangePickerComponent extends AlertStore implements OnInit, OnDestroy {
  protected service = inject(AnullDateRangePickerServices);
  protected translation = inject(TranslationService);
  private cdr = inject(ChangeDetectorRef);

  private isLoadingParams = false;
  private loadParamsPromise: Promise<void> | null = null;
  private paramsLoaded = false;
  private destroy$ = new Subject<void>();
  private isUpdatingFromEffect = false;

  protected t(key: string): string {
    return this.translation.translate(key);
  }

  start = input<Date | null>(null);
  end = input<Date | null>(null);
  minDate = input<Date | null>(null);
  maxDate = input<Date | null>(null);

  startLabel = input<string>(this.t('ACCOUNTING.ANNULLMENT.PLACEHOLDER.DATE_START'));
  endLabel = input<string>(this.t('ACCOUNTING.ANNULLMENT.PLACEHOLDER.DATE_END'));
  placeholder = input<string>('dd/MM/yyyy');

  startChange = output<Date | null>();
  endChange = output<Date | null>();
  rangeChange = output<{ start: Date | null; end: Date | null }>();

  vigenciaChange = output<{ inicio: Date | null; fin: Date | null }>();

  startModel = model<Date | null>(null);
  endModel = model<Date | null>(null);

  systemDate = signal<Date | null>(null);
  vigenciaInicio = signal<Date | null>(null);
  vigenciaFin = signal<Date | null>(null);
  errorVigencia = signal<string | null>(null);

  private fechasInicializadas = false;

  effectiveMinDate = computed(() => {
    const min = this.minDate();
    const vigInicio = this.vigenciaInicio();

    if (min && vigInicio) {
      return min > vigInicio ? min : vigInicio;
    }
    return min || vigInicio || null;
  });

  effectiveMaxDate = computed(() => {
    const max = this.maxDate();
    const vigFin = this.vigenciaFin();

    if (max && vigFin) {
      return max < vigFin ? max : vigFin;
    }
    return max || vigFin || null;
  });

  constructor() {
    super();

    effect(() => {
      const start = this.start();
      const end = this.end();

      if (!this.isUpdatingFromEffect) {
        this.startModel.set(start);
        this.endModel.set(end);
      }
    });

    effect(() => {
      const systemDateNow = this.systemDate();
      const vigInicio = this.vigenciaInicio();
      const vigFin = this.vigenciaFin();

      if (
        !this.fechasInicializadas &&
        systemDateNow !== null &&
        vigInicio !== null &&
        vigFin !== null &&
        !this.isUpdatingFromEffect
      ) {
        this.isUpdatingFromEffect = true;

        const defaultStart = this.getDefaultStart();
        const defaultEnd = this.getDefaultEnd();

        this.startModel.set(defaultStart);
        this.endModel.set(defaultEnd);

        this.isValidRange(defaultStart, defaultEnd);

        this.rangeChange.emit({
          start: defaultStart,
          end: defaultEnd,
        });

        this.fechasInicializadas = true;
        this.isUpdatingFromEffect = false;
        this.cdr.detectChanges();
      }
    });
  }

  ngOnInit(): void {
    this.loadSystemParamsOnce();
  }

  private loadSystemParamsOnce(): void {
    if (this.loadParamsPromise) {
      return;
    }

    if (this.paramsLoaded) {
      return;
    }

    if (this.isLoadingParams) {
      return;
    }

    this.loadParamsPromise = this.performLoadSystemParams();

    this.loadParamsPromise.finally(() => {
      this.loadParamsPromise = null;
    });
  }

  private performLoadSystemParams(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.isLoadingParams = true;

      forkJoin({
        systemDate: this.service.getSystemDate().pipe(
          catchError(() => {
            return of(null);
          }),
        ),
        systemFields: this.service.getSystemFields().pipe(
          catchError(() => {
            return of(null);
          }),
        ),
      })
        .pipe(
          tap((result) => {
            const { systemDate, systemFields } = result;

            if (!systemDate || !systemFields) {
              const errorMsg = !systemDate
                ? 'No se pudo obtener la fecha del sistema'
                : 'No se pudo obtener la vigencia contable';

              this.setError(errorMsg);
              this.systemDate.set(null);
              this.vigenciaInicio.set(null);
              this.vigenciaFin.set(null);

              this.vigenciaChange.emit({
                inicio: null,
                fin: null,
              });

              this.isLoadingParams = false;
              reject(new Error(errorMsg));
              return;
            }

            this.systemDate.set(this.parseDateFromString(systemDate.fechaSistema));

            const vigencia = this.calcularVigenciaActiva({
              vigenciaContable: systemFields.vigenciaContable,
              ultimoMesCerrado: systemFields.ultimoMesCerrado,
              fechaSistema: systemDate.fechaSistema,
            });

            this.vigenciaInicio.set(vigencia.inicio);
            this.vigenciaFin.set(vigencia.fin);

            this.vigenciaChange.emit({
              inicio: vigencia.inicio,
              fin: vigencia.fin,
            });

            this.paramsLoaded = true;
            this.isLoadingParams = false;
            resolve();
          }),
          catchError((error) => {
            const errorMsg = error?.message || 'No se pudo cargar la vigencia activa';
            this.setError(errorMsg);
            this.systemDate.set(null);
            this.vigenciaInicio.set(null);
            this.vigenciaFin.set(null);

            this.vigenciaChange.emit({
              inicio: null,
              fin: null,
            });

            this.isLoadingParams = false;
            reject(error);
            return of(null);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe();
    });
  }

  refreshSystemParams(): void {
    this.paramsLoaded = false;
    this.isLoadingParams = false;
    this.loadParamsPromise = null;
    this.fechasInicializadas = false;
    this.loadSystemParamsOnce();
  }

  isParamsLoaded(): boolean {
    return this.paramsLoaded;
  }

  /**
   * ✅ Método para resetear el estado de carga
   */
  resetParamsState(): void {
    this.paramsLoaded = false;
    this.isLoadingParams = false;
    this.loadParamsPromise = null;
    this.fechasInicializadas = false;
  }

  private isWithinVigencia(date: Date): boolean {
    const vigInicio = this.vigenciaInicio();
    const vigFin = this.vigenciaFin();

    if (!vigInicio || !vigFin) {
      return true;
    }

    const dateStr = this.formatDate(date);
    const inicioStr = this.formatDate(vigInicio);
    const finStr = this.formatDate(vigFin);

    return dateStr >= inicioStr && dateStr <= finStr;
  }

  private isValidRange(start: Date | null, end: Date | null): boolean {
    if (!start || !end) return true;

    if (start > end) {
      this.errorVigencia.set(
        this.translation.translate('ACCOUNTING.ANNULLMENT.VALIDATION.DATE_INVALID'),
      );
      this.setError(this.translation.translate('ACCOUNTING.ANNULLMENT.VALIDATION.DATE_INVALID'));
      return false;
    }

    const isStartValid = this.isWithinVigencia(start);
    const isEndValid = this.isWithinVigencia(end);

    if (!isStartValid || !isEndValid) {
      this.errorVigencia.set(this.translation.translate('ERR-ANU-08'));
      this.setError(this.translation.translate('ERR-ANU-08'));
      return false;
    }

    this.errorVigencia.set(null);
    this.clearAlert();
    return true;
  }

  onStartChange(date: Date | null): void {
    if (this.isUpdatingFromEffect) return;

    if (date && !this.isValidRange(date, this.endModel())) {
      this.startModel.set(this.start());
      return;
    }
    this.startChange.emit(date);
    this.rangeChange.emit({ start: date, end: this.endModel() });
  }

  onEndChange(date: Date | null): void {
    if (this.isUpdatingFromEffect) return;

    if (date && !this.isValidRange(this.startModel(), date)) {
      this.endModel.set(this.end());
      return;
    }
    this.endChange.emit(date);
    this.rangeChange.emit({ start: this.startModel(), end: date });
  }

  onRangeChange(range: { start: Date | null; end: Date | null }): void {
    if (this.isUpdatingFromEffect) return;

    if (!this.isValidRange(range.start, range.end)) {
      this.startModel.set(this.start());
      this.endModel.set(this.end());
      return;
    }
    this.rangeChange.emit(range);
  }

  private calcularVigenciaActiva(params: {
    vigenciaContable: number;
    ultimoMesCerrado: string;
    fechaSistema: string;
  }): { inicio: Date; fin: Date } {
    const fechaSistema = this.parseDateFromString(params.fechaSistema);

    let inicio: Date;

    if (params.ultimoMesCerrado) {
      const parts = params.ultimoMesCerrado.split('-');
      const mes = parseInt(parts[0], 10);
      const year = parseInt(parts[1], 10);
      inicio = new Date(year, mes, 1);
    } else {
      inicio = new Date(params.vigenciaContable, 0, 1);
    }

    const fin = fechaSistema;

    if (inicio > fin) {
      inicio = new Date(fechaSistema.getFullYear(), fechaSistema.getMonth(), 1);
    }

    return { inicio, fin };
  }

  private parseDateFromString(dateStr: string): Date {
    const parts = dateStr.split('-');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1;
    const day = parseInt(parts[2], 10);
    return new Date(year, month, day);
  }

  private getDefaultStart(): Date {
    const now = this.systemDate() ?? new Date();
    const vigInicio = this.vigenciaInicio();

    if (vigInicio) {
      const firstDayOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      if (vigInicio > firstDayOfMonth) {
        return vigInicio;
      }
      return firstDayOfMonth;
    }

    return new Date(now.getFullYear(), now.getMonth(), 1);
  }

  private getDefaultEnd(): Date {
    const now = this.systemDate() ?? new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();
    return new Date(year, month, day);
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.loadParamsPromise = null;
    this.isLoadingParams = false;
  }
}
